import { useState, useEffect } from "react";
import { Routes, Route, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Plus, Edit, Trash2, ArrowLeft, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogEditor = ({ auth }) => {
  return (
    <Routes>
      <Route path="/" element={<BlogList auth={auth} />} />
      <Route path="/new" element={<BlogArticleForm auth={auth} />} />
      <Route path="/edit/:id" element={<BlogArticleForm auth={auth} />} />
    </Routes>
  );
};

const BlogList = ({ auth }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/admin/blog`, {
        auth: { username: auth.username, password: auth.password }
      });
      setArticles(response.data);
    } catch (error) {
      // handled silently
    } finally {
      setLoading(false);
    }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar este artículo?")) return;
    
    try {
      await axios.delete(`${API}/admin/blog/${id}`, {
        auth: { username: auth.username, password: auth.password }
      });
      toast.success("Artículo eliminado");
      fetchArticles();
    } catch (error) {
      toast.error("Error al eliminar el artículo");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="blog-editor"
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Blog</h1>
          <p className="text-[#94a3b8]">Administra los artículos del blog</p>
        </div>
        <Link to="/admin/blog/new" className="btn-primary flex items-center gap-2">
          <Plus className="w-5 h-5" />
          <span>Nuevo Artículo</span>
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#3b82f6] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-4">
          {articles.map((article) => (
            <div key={article.id} className="glass-card p-6 flex items-center gap-6">
              <div className="w-24 h-16 bg-[#0b1221] overflow-hidden flex-shrink-0">
                {article.image && (
                  <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold truncate">{article.title}</h3>
                <p className="text-[#94a3b8] text-sm truncate">{article.excerpt}</p>
                <div className="flex items-center gap-4 mt-1 text-xs text-[#94a3b8]">
                  <span>{article.category}</span>
                  <span>{article.published_at}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate(`/admin/blog/edit/${article.id}`)}
                  className="p-2 text-[#94a3b8] hover:text-white hover:bg-white/5 rounded"
                >
                  <Edit className="w-5 h-5" />
                </button>
                <button
                  onClick={() => deleteArticle(article.id)}
                  className="p-2 text-red-400 hover:bg-red-500/10 rounded"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
          
          {articles.length === 0 && (
            <div className="text-center py-20 text-[#94a3b8]">
              No hay artículos. Crea el primero.
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

const BlogArticleForm = ({ auth }) => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    category: "Tecnología",
    image: "",
    read_time: 5
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Auto-generate slug from title
    if (name === "title") {
      const slug = value
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.post(`${API}/admin/blog`, formData, {
        auth: { username: auth.username, password: auth.password }
      });
      toast.success("Artículo creado correctamente");
      navigate("/admin/blog");
    } catch (error) {
      toast.error("Error al crear el artículo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate("/admin/blog")} className="p-2 text-[#94a3b8] hover:text-white">
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Nuevo Artículo</h1>
          <p className="text-[#94a3b8]">Crea un nuevo artículo para el blog</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="glass-card p-6">
          <div className="grid gap-4">
            <div>
              <label className="block text-[#94a3b8] text-sm mb-2">Título</label>
              <input type="text" name="title" value={formData.title} onChange={handleChange} required className="contact-input w-full" />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-[#94a3b8] text-sm mb-2">Slug (URL)</label>
                <input type="text" name="slug" value={formData.slug} onChange={handleChange} required className="contact-input w-full" />
              </div>
              <div>
                <label className="block text-[#94a3b8] text-sm mb-2">Categoría</label>
                <select name="category" value={formData.category} onChange={handleChange} className="contact-input w-full">
                  <option value="Tecnología">Tecnología</option>
                  <option value="Seguridad">Seguridad</option>
                  <option value="Logística">Logística</option>
                  <option value="Innovación">Innovación</option>
                  <option value="Negocios">Negocios</option>
                </select>
              </div>
              <div>
                <label className="block text-[#94a3b8] text-sm mb-2">Tiempo de lectura (min)</label>
                <input type="number" name="read_time" value={formData.read_time} onChange={handleChange} className="contact-input w-full" />
              </div>
            </div>
            <div>
              <label className="block text-[#94a3b8] text-sm mb-2">Extracto</label>
              <textarea name="excerpt" value={formData.excerpt} onChange={handleChange} required rows={2} className="contact-input w-full resize-none" />
            </div>
            <div>
              <label className="block text-[#94a3b8] text-sm mb-2">Imagen URL</label>
              <input type="url" name="image" value={formData.image} onChange={handleChange} className="contact-input w-full" placeholder="https://images.unsplash.com/..." />
            </div>
            <div>
              <label className="block text-[#94a3b8] text-sm mb-2">Contenido (Markdown)</label>
              <textarea name="content" value={formData.content} onChange={handleChange} required rows={15} className="contact-input w-full font-mono text-sm" placeholder="## Subtítulo&#10;&#10;Tu contenido aquí...&#10;&#10;- Punto 1&#10;- Punto 2" />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <button type="button" onClick={() => navigate("/admin/blog")} className="btn-secondary">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            <span>{saving ? "Guardando..." : "Crear Artículo"}</span>
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default BlogEditor;
